import React, { useEffect, useRef, useState } from 'react'
import { Settings, CheckCircle2, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useToast } from 'contexts/ToastContext'
import { cn } from 'lib/utils'
import { Button } from 'components/ui/Button'
import { Input } from 'components/ui/Input'
import { FIGMA_COLUMN_2, FIGMA_KANBAN_COLUMN, getFigmaThemeFromStepColor } from './figmaKanbanTheme'
import { FigmaKanbanColumnShell } from './FigmaKanbanColumnShell'

interface FigmaKanbanStepColumnProps {
  title: string
  stepColor: string
  count: number
  children: React.ReactNode
  setDroppableRef: (node: HTMLElement | null) => void
  isOver?: boolean
  canDeleteStep?: boolean
  onDeleteOfferStep?: () => void
  minimumScore?: number
  onMinimumScoreChange?: (score: number) => void
}

/** Figma layout with hiring-step colors from the backend (columns 3+). */
export function FigmaKanbanStepColumn({
  title,
  stepColor,
  count,
  children,
  setDroppableRef,
  isOver = false,
  canDeleteStep = false,
  onDeleteOfferStep,
  minimumScore,
  onMinimumScoreChange,
}: FigmaKanbanStepColumnProps) {
  const { t } = useTranslation()
  const { addToast } = useToast()
  const theme = getFigmaThemeFromStepColor(stepColor)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [localScore, setLocalScore] = useState(String(Math.floor(minimumScore ?? 70)))
  const settingsRef = useRef<HTMLDivElement>(null)
  const showScoreControls = minimumScore !== undefined && onMinimumScoreChange
  const currentScore = minimumScore ?? 70

  useEffect(() => {
    if (minimumScore !== undefined) {
      setLocalScore(String(Math.floor(minimumScore)))
    }
  }, [minimumScore])

  useEffect(() => {
    if (!settingsOpen) return
    const handleClickOutside = (event: MouseEvent) => {
      if (settingsRef.current && !settingsRef.current.contains(event.target as Node)) {
        setSettingsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [settingsOpen])

  const showScoreIncreaseError = () => {
    addToast({
      type: 'error',
      title: t('kanban.scoreLimitError', 'Score Limit'),
      message: t('kanban.cannotIncreaseScore', {
        score: currentScore,
        defaultValue: `Minimum score cannot exceed ${currentScore}%`,
      }),
      duration: 4000,
    })
  }

  const validateScoreInput = (inputValue: string): number | null => {
    if (inputValue === '' || isNaN(Number(inputValue))) {
      setLocalScore(String(currentScore))
      return null
    }

    const numValue = Math.floor(Number(inputValue))
    const validatedScore = Math.max(0, Math.min(100, numValue))

    if (validatedScore > currentScore) {
      showScoreIncreaseError()
      setLocalScore(String(currentScore))
      return null
    }

    if (validatedScore !== numValue) {
      setLocalScore(String(validatedScore))
    }

    return validatedScore
  }

  const handleApplyScore = () => {
    if (!onMinimumScoreChange) return
    const validatedScore = validateScoreInput(localScore)
    if (validatedScore === null) return
    onMinimumScoreChange(validatedScore)
    setSettingsOpen(false)
  }

  const hasScoreChanged = Number(localScore) !== minimumScore

  const scoreSettings = showScoreControls ? (
    <div className="relative shrink-0" ref={settingsRef}>
      <button
        type="button"
        onClick={() => setSettingsOpen((open) => !open)}
        className="flex h-6 w-6 items-center justify-center text-[#232323] transition-opacity hover:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#19A6A6]/40 dark:text-foreground"
        aria-label={t('kanban.figmaInterviewScoreSettings', 'Interview AI score settings')}
        aria-expanded={settingsOpen}
        title={t('kanban.figmaInterviewScoreSettings', 'Interview AI score settings')}
      >
        <Settings className="h-6 w-6" strokeWidth={1.5} />
      </button>
      {settingsOpen && (
        <div className="absolute right-0 top-full z-50 mt-1 flex items-center gap-1 rounded-md border border-[#D8D8D8] bg-white p-2 shadow-lg dark:border-border dark:bg-card">
          <span className="text-xs font-bold" style={{ color: theme.accent }}>
            %
          </span>
          <Input
            type="number"
            min={1}
            max={currentScore}
            step={1}
            value={localScore}
            onChange={(e) => setLocalScore(e.target.value.replace(/\D/g, ''))}
            onBlur={() => {
              validateScoreInput(localScore)
            }}
            onKeyDown={(e) => {
              if (['.', ',', 'e', 'E', '+', '-'].includes(e.key)) {
                e.preventDefault()
                return
              }
              if (e.key >= '0' && e.key <= '9') {
                const nextValue = Number(`${e.currentTarget.value}${e.key}`)
                if (!isNaN(nextValue) && nextValue > currentScore) {
                  e.preventDefault()
                  showScoreIncreaseError()
                  setLocalScore(String(currentScore))
                }
              }
              if (e.key === 'Enter') {
                e.currentTarget.blur()
              }
            }}
            className="h-7 w-14 px-1 text-center text-xs"
          />
          {hasScoreChanged && (
            <Button
              type="button"
              size="sm"
              onClick={handleApplyScore}
              className="h-7 w-7 p-0"
              title={t('common.apply', 'Apply')}
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      )}
    </div>
  ) : null

  const headerActions =
    canDeleteStep && onDeleteOfferStep ? (
      <>
        {scoreSettings}
        <button
          type="button"
          onClick={onDeleteOfferStep}
          className="flex h-6 w-6 items-center justify-center rounded text-red-600 transition-opacity hover:bg-red-50 hover:opacity-90 dark:text-red-400 dark:hover:bg-red-900/20"
          aria-label={t('common.delete', 'Delete')}
          title={t('common.delete', 'Delete')}
        >
          <X className="h-4 w-4" strokeWidth={2} />
        </button>
      </>
    ) : (
      scoreSettings
    )

  const footer = showScoreControls ? (
    <div
      className="flex w-full shrink-0 items-center justify-center border-t border-[#D8D8D8] bg-[#F2F2F7] dark:border-border dark:bg-[#1F2430]"
      style={{
        height: FIGMA_KANBAN_COLUMN.footerHeight,
        paddingTop: FIGMA_KANBAN_COLUMN.footerPaddingY,
        paddingBottom: FIGMA_KANBAN_COLUMN.footerPaddingY,
        paddingLeft: FIGMA_KANBAN_COLUMN.footerPaddingX,
        paddingRight: FIGMA_KANBAN_COLUMN.footerPaddingX,
        gap: FIGMA_KANBAN_COLUMN.footerGap,
      }}
    >
      <p className={cn('w-full text-center', FIGMA_COLUMN_2.footerTextClass)}>
        {t('kanban.figmaAiScoreFooter', 'Score IA : {{score}}%', { score: Math.floor(minimumScore ?? 70) })}
      </p>
    </div>
  ) : undefined

  return (
    <FigmaKanbanColumnShell
      title={title}
      count={count}
      theme={theme}
      setDroppableRef={setDroppableRef}
      isOver={isOver}
      headerActions={headerActions}
      footer={footer}
    >
      {children}
    </FigmaKanbanColumnShell>
  )
}
